import { useContext, useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { portalColors, portalRadius } from './portalStyles';
import { usePortalChecklist } from './usePortalChecklist';
import { PreviewReadOnlyContext } from './previewClient';
import type { ChecklistItem } from '../../types/crewPortal';

interface Props {
  checklist: ChecklistItem[];
  jobId: string;
  accentColor: string;
  firstName: string;
  /** §N — rows render unchecked + non-interactive; progress reads "0 of n". */
  readOnly?: boolean;
}

export function ChecklistSection({ checklist, jobId, accentColor, firstName, readOnly }: Props) {
  const ctxReadOnly = useContext(PreviewReadOnlyContext);
  const isReadOnly = readOnly ?? ctxReadOnly;
  // hook called unconditionally (rules-of-hooks); its state is ignored in read-only
  const { getCheckedItems, toggleItem, getCheckedCount, isComplete } = usePortalChecklist();
  const checked = isReadOnly ? {} as Record<number, boolean> : getCheckedItems(jobId);
  const checkedCount = isReadOnly ? 0 : getCheckedCount(jobId, checklist.length);
  const done = isReadOnly ? false : isComplete(jobId, checklist.length);

  return (
    <div style={{ marginTop: 16 }}>
      {/* Progress */}
      <div style={{ fontSize: 12, color: portalColors.secondary, marginBottom: 6 }}>
        {checkedCount} of {checklist.length} complete
      </div>
      <div style={{ height: 4, background: portalColors.divider, borderRadius: 2, marginBottom: 12, overflow: 'hidden' }}>
        <div style={{
          height: '100%', background: accentColor, borderRadius: 2,
          width: `${checklist.length > 0 ? (checkedCount / checklist.length) * 100 : 0}%`,
          transition: 'width 400ms ease',
        }} />
      </div>

      {/* Items */}
      {checklist.map((item, i) => {
        const isChecked = !!checked[i];
        return (
          <div key={i}>
            <ChecklistRow
              label={item.label}
              notes={item.notes ?? null}
              checked={isChecked}
              accentColor={accentColor}
              onToggle={isReadOnly ? () => {} : () => toggleItem(jobId, i)}
              interactive={!isReadOnly}
            />
            {i < checklist.length - 1 && (
              <div style={{ height: 0.5, background: portalColors.subtleDivider, marginLeft: 36 }} />
            )}
          </div>
        );
      })}

      {/* Completion */}
      {done && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
          padding: '10px 0',
        }}>
          <CheckCircle size={18} color={accentColor} />
          <span style={{ fontSize: 14, color: accentColor, fontWeight: 500 }}>
            All done — nice work, {firstName}
          </span>
        </div>
      )}
    </div>
  );
}

function ChecklistRow({ label, notes, checked, accentColor, onToggle, interactive = true }: {
  label: string; notes: string | null; checked: boolean; accentColor: string; onToggle: () => void;
  interactive?: boolean;
}) {
  const [animating, setAnimating] = useState(false);

  const handleClick = () => {
    if (!interactive) return;
    setAnimating(true);
    onToggle();
    setTimeout(() => setAnimating(false), 200);
  };

  return (
    <div
      onClick={handleClick}
      style={{ display: 'flex', gap: 12, padding: '10px 0', cursor: interactive ? 'pointer' : 'default', minHeight: 44, alignItems: 'flex-start' }}
    >
      {/* Checkbox */}
      <div style={{
        width: 24, height: 24, borderRadius: portalRadius.checkbox, flexShrink: 0, marginTop: 1,
        border: checked ? 'none' : `2px solid ${portalColors.veryMuted}`,
        background: checked ? accentColor : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transform: animating ? 'scale(1.15)' : 'scale(1)',
        transition: 'transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1), background 150ms',
      }}>
        {checked && (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 7L6 10L11 4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>

      {/* Label + notes */}
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: 15, color: portalColors.primary,
          opacity: checked ? 0.5 : 1,
          textDecoration: checked ? 'line-through' : 'none',
          textDecorationThickness: '1px',
          transition: 'opacity 200ms',
        }}>{label}</div>
        {notes && (
          <div style={{ fontSize: 12, color: portalColors.muted, marginTop: 2 }}>{notes}</div>
        )}
      </div>
    </div>
  );
}
